import { CUSTOMER_EVENTS } from 'src/boot/tracking/events'
import Model from 'src/models/Model'

/**
 * Track an event in **one** of the following, whichever is first found:
 * 1. Google Tag (gtag.js)
 * 2. Google Tag Manager (gtm.js)
 * 3. Google Analytics (ga.js) - Legacy, replaced by Google Tag. Set to stop
 *    working in July 2023-24.
 *
 * We want to send events to **one** of the above because in the unlikely case
 * that a client as more than one of the above, one of them usually forwards to
 * the other and we want to avoid sending duplicate events.
 *
 * Note that Google Tag is not the same as Google Tag Manager. They both use
 * a `window.dataLayer` array, but with different arguments. Typically, we expect
 * only one of these to be present, since they overlap in functionality.
 * https://developers.google.com/analytics/devguides/collection/ga4/tag-options
 */
export function trackEventViaGoogle (name, data, skipExtraEvents = false) {
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', name, data)
    } catch (e) {
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to track event via Google Tag', e)
      }
    }
  } else if (window.dataLayer) {
    // We can provide a GTM configuration that is preconfigured to forward our custom events
    // to GA as e-commerce "recommended" events. Assuming the client uses this, we want to
    // avoid sending duplicate events. We can perhaps configure GTM to set some window flag
    // to determine whether they are using the preconfigured setup we provide.
    if (window.indyGtmConfigured) {
      skipExtraEvents = true
    }
    try {
      window.dataLayer.push({ ...data, event: name })
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to track event via Google Tag Manager', e)
    }
  } else if (typeof window.ga === 'function') {
    try {
      window.ga('send', {
        hitType: 'event',
        eventCategory: 'Track Event',
        eventAction: name
      })
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to track event via Google Analytics (Legacy)', e)
    }
  }

  if (!skipExtraEvents) {
    // Prefer to send Google's "recommended events" when possible. See below.
    const payload = getRecommendedEventFromIndyEvent(name, data)
    if (payload) {
      trackEventViaGoogle(payload.name, payload.data, true)
    }
  }
}

export function trackPageViaGoogle (pagePath, data) {
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('set', 'page_path', pagePath)
      window.gtag('event', 'page_view', data)
    } catch (e) {
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to track page view via Google Tag', e)
      }
    }
  } else if (window.dataLayer) {
    try {
      window.dataLayer.push({ ...data, event: 'pageview', path: pagePath })
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to track page view via Google Tag Manager', e)
    }
  } else if (typeof window.ga === 'function') {
    try {
      window.ga('send', {
        hitType: 'pageview',
        page: pagePath,
        title: data.name
      })
    } catch (e) {
      // eslint-disable-next-line no-undef
      Rollbar.error('Failed to track page view via Google Analytics (Legacy)', e)
    }
  }
}

/**
 * Get an event payload object containing the `name` and `data` for a
 * Google "recommended event" that resembles one of our own internal events.
 * Returns `null` if no such event exists. We want to send Google's predefined
 * recommended events whenever possible to enable additional analytics features.
 *
 * Documentation: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */
export function getRecommendedEventFromIndyEvent (indyEventName, indyEventData = {}) {
  // At least `name` must be set to trigger a standard event.
  const recommendedEvent = { name: null, data: null }
  const currentSite = Model.configuration?.currentSite

  switch (indyEventName) {
    case CUSTOMER_EVENTS.ADDED_FAVORITE:
      recommendedEvent.name = 'add_to_wishlist'
      break
    case CUSTOMER_EVENTS.ADDED_USUAL_ORDER:
      recommendedEvent.name = 'add_to_cart'
      break
    case CUSTOMER_EVENTS.REMOTE_ORDER_PLACED:
      recommendedEvent.name = 'purchase'
      recommendedEvent.data = {
        transaction_id: indyEventData.orderId,
        value: indyEventData.total,
        tax: indyEventData.tax,
        currency: indyEventData.currency ? _.toUpper(indyEventData.currency) : undefined,
        items: _.map(indyEventData.items, item => {
          return {
            affiliation: currentSite?.name,
            item_id: item.ticketType || item.type,
            item_name: item.name,
            item_category: item.type,
            quantity: item.quantity,
            price: item.total,
            discount: item.discount
          }
        })
      }
      break
    case CUSTOMER_EVENTS.SUBSCRIBED_TO_MARKETING_LISTS:
      recommendedEvent.name = 'generate_lead'
      break
    case 'Viewed Showing Checkout':
      recommendedEvent.name = 'begin_checkout'
      break
  }

  return recommendedEvent.name ? recommendedEvent : null
}
